// Bugsnag setup for the admin app. Isomorphic: the same module runs on the Next.js
// server (route handlers, server components) and in the browser, tagging each with a
// distinct `appType` so they can be triaged separately within the shared project.
//
// Uses the SAME Bugsnag project/key as the backend (the project is bound to this git
// repo). No-op when NEXT_PUBLIC_BUGSNAG_API_KEY is unset (local dev / disabled).
import Bugsnag from '@bugsnag/js';

// Must be NEXT_PUBLIC_* so the key + stage reach the browser bundle at build time.
const apiKey = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY || '';
const releaseStage = process.env.NEXT_PUBLIC_RELEASE_STAGE || 'development';
const isServer = typeof window === 'undefined';

export const bugsnagEnabled = Boolean(apiKey);

// Start once per runtime. `plugins` lets the browser pass the React plugin (for the
// error boundary); the server starts without it.
export function startBugsnag(plugins: unknown[] = []): void {
  if (!bugsnagEnabled || Bugsnag.isStarted()) return;
  Bugsnag.start({
    apiKey,
    releaseStage,
    appType: isServer ? 'admin-server' : 'admin-client',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    plugins: plugins as any,
  });
}

// Report a handled error. Bugsnag's automatic detection only sees *unhandled* errors,
// so anything we catch (e.g. the swallowed Bedrock insights error) must be sent here.
export function notifyError(
  error: unknown,
  metadata?: Record<string, unknown>,
  severity: 'error' | 'warning' | 'info' = 'error'
): void {
  if (!bugsnagEnabled) return;
  if (!Bugsnag.isStarted()) startBugsnag();
  Bugsnag.notify(error as Error, (event) => {
    event.severity = severity;
    if (metadata) event.addMetadata('context', metadata);
  });
}
